import { Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerService } from '../../services/customer.service';
import { OtpComponent } from '../otp/otp.component';
import { PreloaderComponent } from '../preloader/preloader.component';
import { AuthService } from '../../services/auth.service';
import { OtpRequestClientDto } from '../../dtos/otp-request.dto';
import { CustomValidators } from '../../validators/validators';
import { DEFAULT_ERROR_TEXT, OTP_LENGTH } from '../../constants';
import { ResetPasswordOtpDto } from '../../dtos/reset-password-otp.dto';
import { ResetPasswordConfirmDto } from '../../dtos/reset-password-confirm.dto';


class ResetPasswordForm {
  login: FormControl<string>;
  password: FormControl<string>;
  repeatPassword: FormControl<string>;
  otpDigits: FormArray<FormControl<string>>;
}

type ResetPasswordFormState = 'otp' | 'password' | 'success';

@Component({
  selector: 'tc-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  imports: [
    TranslatePipe,
    OtpComponent,
    PreloaderComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class ResetPasswordComponent implements OnInit {
  customerService = inject(CustomerService);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);

  login = input.required<string>();
  success = output();

  state = signal<ResetPasswordFormState>('otp');
  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  otpRequest = signal<OtpRequestClientDto>(null);
  private isFormErrorsVisible = signal<boolean>(false);

  form: FormGroup<ResetPasswordForm>;

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    const passwordControl = new FormControl<string>('', Validators.required);

    this.form = new FormGroup({
      login: new FormControl<string>(this.login(), Validators.required),
      password: passwordControl,
      repeatPassword: new FormControl<string>('', CustomValidators.passwordConfirm(passwordControl)),
      otpDigits: new FormArray(
        new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
      ),
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetErrors());

    this.resetPasswordInit();
  }

  submit(): void {
    if (this.isLoading()) {
      return;
    }

    this.resetErrors();

    switch (this.state()) {
      case 'otp':
        this.resetPasswordOtp();
        break;
      case 'password':
        this.resetPasswordConfirm();
        break;
      case 'success':
        this.success.emit();
        break;
    }
  }

  resetPasswordInit(): void {
    this.resetErrors();
    if (this.form.controls.login.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    this.isLoading.set(true);
    this.authService.resetPasswordInit(this.form.controls.login.getRawValue())
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.otpRequest.set(response.data);
          this.resetErrors();
          this.state.set('otp');
        },
        error: error => this.error.set(error.error?.message || DEFAULT_ERROR_TEXT),
      });
  }

  resetPasswordOtp(): void {
    this.resetErrors();
    if (this.form.controls.otpDigits.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    const dto: ResetPasswordOtpDto = {
      otpCode: this.form.controls.otpDigits.value.join(''),
      requestId: this.otpRequest().requestId,
    };

    this.isLoading.set(true);
    this.authService.resetPasswordOtp(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.resetErrors();
          this.state.set('password');
        },
        error: error => this.error.set(error.error?.message || DEFAULT_ERROR_TEXT),
      });
  }

  resetPasswordConfirm(): void {
    this.resetErrors();
    if (this.form.controls.password.invalid || this.form.controls.repeatPassword.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    const dto: ResetPasswordConfirmDto = {
      requestId: this.otpRequest().requestId,
      password: this.form.controls.password.getRawValue(),
    };

    this.isLoading.set(true);
    this.authService.resetPasswordConfirm(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.state.set('success'),
        error: error => this.error.set(error.error?.message || DEFAULT_ERROR_TEXT),
      });
  }

  isControlInvalid(control: AbstractControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  resetErrors(): void {
    this.isFormErrorsVisible.set(false);
    this.error.set(null);
  }
}
